import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { BUSINESS_PLAN_STATUS } from 'constants/businessPlan';
import dayjs from 'dayjs';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessPlanFileUpload, useBusinessPlanFileUploadComplete } from 'services/businessPlanFiles/hooks';
import { useImageUpload } from 'services/files/hooks/useImageUpload';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan } from 'store/useBusinessToolStore/selectors';

import { IBusinessPlanEntity, IBusinessPlanStatus } from 'interfaces/businessPlan';

import { BlackToast } from 'components/_basic/BlackToast/BlackToast';
import { InviteAssistantsModal } from 'components/InviteAssistantsModal/InviteAssistantsModal';
import { useModalContext } from 'components/ModalManager';
import { PDFStatusModal } from 'components/PDFStatusModal/PDFStatusModal';
import { IPDFUploadStatus, PDF_UPLOAD_STATUS } from 'components/PDFStatusModal/types';

import { getValidatedMetadata } from 'helpers/metadataParser';
import { copyPdfLink, viewDocHandler } from 'helpers/viewDocHandler';

import { TrackFn } from './usePromiseStatus';

interface IUseSharePDF {
  uuid: string;
  pdfGenerationHandler: VoidFunction;
  track: TrackFn;
  metadata: IBusinessPlanEntity['metadata'];
  docTitle: string;
  status?: IBusinessPlanStatus;
}

export const useSharePDF = ({ uuid, pdfGenerationHandler, track, metadata, docTitle, status }: IUseSharePDF) => {
  const [isPdfGenerated, setPdfGenerated] = useState(() => status === BUSINESS_PLAN_STATUS.PUBLISHED);
  const { showModal, hideModal } = useModalContext();

  const businessPlanPDFUpload = useBusinessPlanFileUpload();
  const pdfUploadMutation = useImageUpload();
  const businessPlanPDFUploadComplete = useBusinessPlanFileUploadComplete();
  const updateBusinessPlan = useUpdateBusinessPlan();
  const businessPlan = useBusinessToolStore(getBusinessPlan);

  const displayDocUploadStatusModal = (status: IPDFUploadStatus) => {
    showModal(PDFStatusModal, {
      status,
      onClose: hideModal,
      onFileUpload: pdfGenerationHandler,
      onViewDoc: () => viewDocHandler(uuid),
    });
  };

  const copyLinkHandler = useCallback(() => {
    copyPdfLink(uuid);
    toast(<BlackToast />, {
      position: 'top-center',
    });
  }, [uuid]);

  const inviteAssistantsModal = () => {
    showModal(InviteAssistantsModal, {
      onClose: hideModal,
      id: uuid,
    });
  };

  const handleFileUpload = async (pdfUrl: string) => {
    try {
      hideModal();
      const response = await fetch(pdfUrl);
      const pdfBlob = await response.blob();

      const { url: startUrl, fileUuid } = await track(
        businessPlanPDFUpload.mutateAsync({
          id: uuid,
          filePayload: {
            filename: docTitle,
            contentType: pdfBlob.type,
            size: pdfBlob.size,
          },
        }),
      );

      await track(pdfUploadMutation.mutateAsync({ file: pdfBlob, url: startUrl }));

      const { url: shortUrl } = await track(
        businessPlanPDFUploadComplete.mutateAsync({
          id: uuid,
          fileUuid: fileUuid,
        }),
      );

      updateBusinessPlan
        .mutateAsync({
          id: uuid,
          body: {
            metadata: JSON.stringify({
              ...getValidatedMetadata(metadata),
              pdfShareLink: shortUrl,
              pdfCreatedAt: dayjs().toISOString(),
              updateOnlinePDFCount: (businessPlan.metadata.updateOnlinePDFCount ?? 0) + 1,
            }),
            status: BUSINESS_PLAN_STATUS.PUBLISHED,
          },
        })
        .then(() => setPdfGenerated(true));

      displayDocUploadStatusModal(PDF_UPLOAD_STATUS.SUCCESS);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(error);
      }
      displayDocUploadStatusModal(PDF_UPLOAD_STATUS.FAIL);
    }
  };

  return { copyLinkHandler, handleFileUpload, inviteAssistantsModal, isPdfGenerated };
};
